/* Page transitions — dreamwork-style liquify dissolve on Turbo Drive.
 *
 * Outgoing #sm-view becomes a fixed ghost that liquifies (SVG turbulence
 * displacement + blur, enveloped per-frame) and drifts up as it fades.
 * Incoming #sm-view snaps to .sm-enter (opacity 0) then coalesces from mist.
 * Opacity/transform ride CSS; the mist filter is JS-driven so the middle of
 * the dissolve lingers hazy. No body perspective (traps the fixed canvas).
 *
 * Cross-layout (survey ↔ article): ghost widths are frozen at capture so
 * body.sm-role-* swaps cannot shrink the clone.
 *
 * Capture runs in before-render (before-cache would wipe a before-visit
 * ghost). Preview+final: one dissolve owns the ghost; never re-capture or
 * re-pose enter mid-flight. Visibility hide snaps finish — background tabs
 * throttle rAF/timers and otherwise leave displacement filters stuck
 * (looks like permanent double-vision).
 */

import { pulseDreambg } from "./dreambg";

const DREAM_MS = 820;
const ENTER_DELAY_MS = 110;

const reducedMotion = () =>
    window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches;

const view = () => document.getElementById("sm-view");
const ghostRoot = () => document.getElementById("sm-ghost-root");

function fxNode(filterId, tag) {
    return document.querySelector("#" + filterId + " " + tag);
}

function smoothstep(x) {
    return x * x * (3 - 2 * x);
}

let dissolveRaf = 0;
let dissolveTimer = 0;
let dissolveGen = 0;
let dissolving = false;
let enterReleased = false;

function resetFxScales() {
    const dOut = fxNode("sm-dissolve-out", "feDisplacementMap");
    const bOut = fxNode("sm-dissolve-out", "feGaussianBlur");
    const dIn = fxNode("sm-dissolve-in", "feDisplacementMap");
    const bIn = fxNode("sm-dissolve-in", "feGaussianBlur");
    if (dOut) dOut.setAttribute("scale", "0");
    if (bOut) bOut.setAttribute("stdDeviation", "0");
    if (dIn) dIn.setAttribute("scale", "0");
    if (bIn) bIn.setAttribute("stdDeviation", "0");
}

function clearLiveViewFx() {
    const el = view();
    if (!el) return;
    el.style.filter = "";
    el.classList.remove("sm-enter", "sm-leave");
}

function clearDissolve(keepGhost) {
    if (dissolveRaf) {
        cancelAnimationFrame(dissolveRaf);
        dissolveRaf = 0;
    }
    if (dissolveTimer) {
        clearTimeout(dissolveTimer);
        dissolveTimer = 0;
    }
    const root = ghostRoot();
    if (root) {
        Array.from(root.children).forEach((c) => {
            if (c !== keepGhost && c.parentNode === root) {
                root.removeChild(c);
            }
        });
    }
    if (!keepGhost) {
        clearLiveViewFx();
        dissolving = false;
        enterReleased = false;
        resetFxScales();
    }
}

/** Force-complete any in-flight dissolve (blur / interrupt / safety). */
function snapFinish() {
    if (!dissolving && !ghostRoot()?.querySelector(".sm-ghost")) {
        clearLiveViewFx();
        resetFxScales();
        return;
    }
    dissolveGen += 1;
    if (dissolveRaf) {
        cancelAnimationFrame(dissolveRaf);
        dissolveRaf = 0;
    }
    if (dissolveTimer) {
        clearTimeout(dissolveTimer);
        dissolveTimer = 0;
    }
    const root = ghostRoot();
    if (root) root.replaceChildren();
    clearLiveViewFx();
    dissolving = false;
    enterReleased = false;
    resetFxScales();
}

function activeGhost() {
    const root = ghostRoot();
    return root && root.querySelector(".sm-ghost");
}

function captureGhost() {
    const el = view();
    const root = ghostRoot();
    if (!el || !root || reducedMotion()) return null;

    // Never replaceChildren over an in-flight dissolve (would mount a
    // preview-destination clone and leave it stuck).
    const existing = root.querySelector(".sm-ghost");
    if (existing) return existing;

    const rect = el.getBoundingClientRect();
    if (rect.width < 2 || rect.height < 2) return null;

    const liveSurvey = el.querySelector(".cg-survey");
    const liveMain = el.querySelector(".cg-main");
    const surveyW = liveSurvey ? liveSurvey.getBoundingClientRect().width : 0;
    const mainW = liveMain ? liveMain.getBoundingClientRect().width : 0;

    const ghost = el.cloneNode(true);
    ghost.removeAttribute("id");
    ghost.classList.remove("sm-enter", "sm-leave");
    ghost.className = "sm-ghost";
    ghost.querySelectorAll("iframe, canvas, video").forEach((n) => n.remove());
    ghost.style.top = rect.top + "px";
    ghost.style.left = rect.left + "px";
    ghost.style.width = rect.width + "px";
    ghost.style.height = rect.height + "px";

    const gSurvey = ghost.querySelector(".cg-survey");
    if (gSurvey && surveyW >= 2) {
        gSurvey.style.boxSizing = "border-box";
        gSurvey.style.width = surveyW + "px";
        gSurvey.style.maxWidth = surveyW + "px";
    }
    const gMain = ghost.querySelector(".cg-main");
    if (gMain && mainW >= 2) {
        gMain.style.boxSizing = "border-box";
        gMain.style.width = mainW + "px";
        gMain.style.maxWidth = mainW + "px";
    }

    root.replaceChildren(ghost);
    return ghost;
}

function poseEnter(newBody) {
    const incoming = newBody && newBody.querySelector("#sm-view");
    if (!incoming) return;
    incoming.classList.remove("sm-leave");
    incoming.classList.add("sm-enter");
}

function runDissolve(ghost) {
    if (!ghost || !ghost.isConnected) return;

    clearDissolve(ghost);
    const gen = ++dissolveGen;
    dissolving = true;
    enterReleased = false;

    const live = view();
    if (reducedMotion() || !live) {
        dissolving = false;
        enterReleased = false;
        if (ghost.isConnected) ghost.parentNode.removeChild(ghost);
        clearLiveViewFx();
        return;
    }

    ghost.style.filter = "url(#sm-dissolve-out)";
    live.style.filter = "url(#sm-dissolve-in)";
    live.classList.add("sm-enter");
    void live.offsetWidth;

    const dOut = fxNode("sm-dissolve-out", "feDisplacementMap");
    const bOut = fxNode("sm-dissolve-out", "feGaussianBlur");
    const tOut = fxNode("sm-dissolve-out", "feTurbulence");
    const dIn = fxNode("sm-dissolve-in", "feDisplacementMap");
    const bIn = fxNode("sm-dissolve-in", "feGaussianBlur");
    const tIn = fxNode("sm-dissolve-in", "feTurbulence");
    const seed = 7 + (Math.floor(Date.now() / 1000) % 17);
    if (tOut) tOut.setAttribute("seed", String(seed));
    if (tIn) tIn.setAttribute("seed", String(seed + 3));

    pulseDreambg();

    // Double rAF: paint opacity-0 once, then release (dreamwork true-zero).
    // Always re-query #sm-view — preview→final swaps detach the old node.
    requestAnimationFrame(() => {
        requestAnimationFrame(() => {
            if (gen !== dissolveGen) return;
            const el = view();
            if (el) el.classList.remove("sm-enter");
            if (ghost.isConnected) ghost.classList.add("sm-ghost-out");
            enterReleased = true;
        });
    });

    const t0 = performance.now();
    const finish = () => {
        if (gen !== dissolveGen) return;
        if (dissolveRaf) {
            cancelAnimationFrame(dissolveRaf);
            dissolveRaf = 0;
        }
        if (dissolveTimer) {
            clearTimeout(dissolveTimer);
            dissolveTimer = 0;
        }
        if (ghost.isConnected) ghost.parentNode.removeChild(ghost);
        // Live node may be a post-final-swap element — never use closed-over el.
        clearLiveViewFx();
        dissolving = false;
        enterReleased = false;
        resetFxScales();
    };

    function stepFx(now) {
        if (gen !== dissolveGen) return;
        const u = Math.min(1, (now - t0) / DREAM_MS);
        const eo = smoothstep(u);
        if (dOut) dOut.setAttribute("scale", (eo * 22).toFixed(2));
        if (bOut) bOut.setAttribute("stdDeviation", (eo * 3.4).toFixed(2));
        const ui = Math.min(1, Math.max(0, (now - t0 - ENTER_DELAY_MS) / (DREAM_MS - ENTER_DELAY_MS)));
        const ei = smoothstep(ui);
        if (dIn) dIn.setAttribute("scale", ((1 - ei) * 16).toFixed(2));
        if (bIn) bIn.setAttribute("stdDeviation", ((1 - ei) * 2.8).toFixed(2));
        const bf = (0.009 + eo * 0.008).toFixed(4);
        if (tOut) tOut.setAttribute("baseFrequency", bf);
        if (tIn) tIn.setAttribute("baseFrequency", bf);
        // Keep mist filter on whatever #sm-view is live after Turbo swaps.
        const el = view();
        if (el && dissolving) el.style.filter = "url(#sm-dissolve-in)";
        if (u < 1) dissolveRaf = requestAnimationFrame(stepFx);
        else finish();
    }
    dissolveRaf = requestAnimationFrame(stepFx);
    // Wall-clock backup; still throttled in hidden tabs — see visibility hook.
    dissolveTimer = window.setTimeout(finish, DREAM_MS + 350);
}

export function initPageTransitions() {
    document.addEventListener("turbo:before-visit", () => {
        if (reducedMotion()) return;
        // Interrupt prior dissolve so the next hop gets a fresh outgoing ghost.
        if (dissolving || activeGhost()) snapFinish();
    });

    document.addEventListener("turbo:before-render", (ev) => {
        const newBody = ev.detail && ev.detail.newBody;

        if (reducedMotion()) {
            if (newBody) {
                const incoming = newBody.querySelector("#sm-view");
                if (incoming) incoming.classList.remove("sm-enter", "sm-leave");
            }
            return;
        }

        if (newBody) {
            newBody.style.background = "#0b0f19";
            const liveCanvas = document.getElementById("sm-dreambg");
            const nextCanvas = newBody.querySelector("#sm-dreambg");
            if (liveCanvas && nextCanvas && liveCanvas !== nextCanvas) {
                nextCanvas.replaceWith(liveCanvas);
            }
        }

        if (dissolving) {
            // Final render after preview: keep mist on the NEW #sm-view, but
            // do not re-stamp .sm-enter once the enter phase was released.
            if (newBody) {
                const incoming = newBody.querySelector("#sm-view");
                if (incoming) {
                    incoming.style.filter = "url(#sm-dissolve-in)";
                    if (!enterReleased) incoming.classList.add("sm-enter");
                    else incoming.classList.remove("sm-enter");
                }
            }
            return;
        }

        if (!activeGhost()) captureGhost();

        if (newBody) poseEnter(newBody);
    });

    document.addEventListener("turbo:render", () => {
        if (reducedMotion()) {
            clearLiveViewFx();
            return;
        }
        const ghost = activeGhost();
        if (ghost && ghost.isConnected) {
            if (!dissolving) runDissolve(ghost);
            else {
                // Mid-flight body swap: attach mist to the live node only.
                const el = view();
                if (el) {
                    el.style.filter = "url(#sm-dissolve-in)";
                    if (!enterReleased) {
                        el.classList.add("sm-enter");
                        void el.offsetWidth;
                    } else {
                        el.classList.remove("sm-enter");
                    }
                }
            }
        } else if (!dissolving) {
            clearLiveViewFx();
        }
    });

    document.addEventListener("turbo:load", () => {
        if (!dissolving) {
            const el = view();
            if (el && !el.style.filter) el.classList.remove("sm-enter", "sm-leave");
        }
        // Always exempt — even mid-dissolve — so preview path stays rare.
        try {
            if (window.Turbo && Turbo.cache && typeof Turbo.cache.exemptPageFromCache === "function") {
                Turbo.cache.exemptPageFromCache();
            }
        } catch (_) { /* turbo version without cache API */ }
    });

    // Snapshot for cache must not wipe an in-flight ghost. Strip enter +
    // filter from the live view so the cached HTML isn't stuck misty/invisible.
    document.addEventListener("turbo:before-cache", () => {
        if (dissolving) {
            const el = view();
            if (el) {
                el.style.filter = "";
                el.classList.remove("sm-enter", "sm-leave");
            }
            return;
        }
        clearDissolve();
    });

    window.addEventListener("pageshow", (ev) => {
        if (!ev.persisted) return;
        snapFinish();
    });

    // Hidden tabs throttle rAF + timers → displacement filter stuck mid-flight
    // reads as permanent double-vision. Snap clean on hide only (not window
    // blur — omnibox/DevTools/focus-follows-mouse would abort healthy dissolves).
    document.addEventListener("visibilitychange", () => {
        if (document.visibilityState === "hidden" && (dissolving || activeGhost())) {
            snapFinish();
        }
    });

    clearLiveViewFx();
}

export default initPageTransitions;
